// The editor's state beyond the text: the layout (mapped through every change), settings, and the pending states the
// Swift session keeps (`pendingEmphasis`, `pending` screenplay element).
import { Annotation, StateEffect, StateField, type EditorState, type Transaction } from '@codemirror/state';
import type { ClassifiedElement } from '@writing-suite/core';
import { Layout, type Piece } from './layout.ts';

/** Edits made exactly as given (the editor's own commands, balanced fixes, split pieces): never balanced again, as
 *  the Swift views' `replaceRun` edits aren't (`decide(balance: false)`). */
export const Exact = Annotation.define<boolean>();
/** Changes that came from the documents (`setFragments`, `applyExternal`): not reported, not balanced, not undoable. */
export const External = Annotation.define<boolean>();
/** A selection put exactly where given (`setCaretExactly`): the caret rules leave it alone. */
export const ExactCaret = Annotation.define<boolean>();

// MARK: layout

/** Updates the pieces (titles, kinds, openers) of the layout in place. */
export const setPieces = StateEffect.define<readonly Piece[]>();

/** Replaces the layout (a new selection of fragments, or new titles/openers/kinds for the same ones). */
export const setLayout = StateEffect.define<Layout>();
export const layoutField = StateField.define<Layout>({
  create: () => Layout.empty,
  update(layout, tr) {
    for (const e of tr.effects) if (e.is(setLayout)) return e.value;
    let next = tr.docChanged ? layout.map(tr.changes) : layout;
    for (const e of tr.effects) if (e.is(setPieces)) next = next.withPieces(e.value);
    return next;
  },
});

export const layoutOf = (state: EditorState) => state.field(layoutField);

/** The fragments the writer has locked (`ScriveningsSession.lockedFragments`): the filter refuses edits in them. */
export const setLockedFragments = StateEffect.define<readonly string[]>();
export const lockedField = StateField.define<ReadonlySet<string>>({
  create: () => new Set(),
  update(value, tr) {
    for (const e of tr.effects) if (e.is(setLockedFragments)) return new Set(e.value);
    return value;
  },
});

/** Whether replacing [a, b) would touch a locked fragment's body. */
export function isLocked(state: EditorState, a: number, b: number): boolean {
  const locked = state.field(lockedField, false);
  if (!locked?.size) return false;
  const layout = state.field(layoutField);
  return layout.plan(a, b, '').some((e) => locked.has(layout.pieces[e.index].id));
}
export const piecesField = StateField.define<number>({
  // A generation count, bumped when the pieces change, so decorations know to rebuild dividers.
  create: () => 0,
  update(v, tr) { for (const e of tr.effects) if (e.is(setPieces) || e.is(setLayout)) return v + 1; return v; },
});

// MARK: settings

export interface Settings {
  /** Show emphasis asterisks, escapes and span brackets (faintly) instead of hiding them. */
  showMarks: boolean;
  /** How the project's scripts are laid out (`ScriptFormat`): 'screen', 'multicam', 'stage-us', 'stage-uk' or 'radio'. */
  scriptFormat?: string;
  /** The project is a musical (`EditorTheme.musical`): counts only in a stage format. */
  musical?: boolean;
}
export const setSettings = StateEffect.define<Partial<Settings>>();
export const settingsField = StateField.define<Settings>({
  create: () => ({ showMarks: false }),
  update(v, tr) {
    for (const e of tr.effects) if (e.is(setSettings)) v = { ...v, ...e.value };
    return v;
  },
});
export const showsMarks = (state: EditorState) => state.field(settingsField).showMarks;
/** The project's script format (`EditorTheme.scriptFormat`): 'screen' unless it's a play's. */
export const scriptFormatOf = (state: EditorState) => state.field(settingsField).scriptFormat ?? 'screen';
export const isStage = (format: string) => format === 'stage-us' || format === 'stage-uk';
/** `ScriptFormat.isPlayLike`: a stage play or a radio play, which take a play's keys and elements. */
export const isPlayLikeFormat = (format: string) => isStage(format) || format === 'radio';
/** `ScriveningsSession.isMusical`: a play that's a musical (Song after the play's six elements, ⌘7). */
export const isMusical = (state: EditorState) => state.field(settingsField).musical === true && isStage(scriptFormatOf(state));

// MARK: pending emphasis (`ScriveningsSession.pendingEmphasis`)

/** ⌘I/⌘B with nothing selected: what's typed next at `pos` goes in `marker`s (italic `*`, bold `**`, both `***`).
 *  Nothing is inserted until then; any edit or caret move forgets it. */
export interface PendingEmphasis { pos: number; marker: string }
export const setPendingEmphasis = StateEffect.define<PendingEmphasis | null>();
export const pendingEmphasisField = StateField.define<PendingEmphasis | null>({
  create: () => null,
  update(v, tr) {
    for (const e of tr.effects) if (e.is(setPendingEmphasis)) return e.value;
    if (!v) return v;
    if (tr.docChanged) return null;
    if (tr.selection) {
      const m = tr.selection.main;
      if (!m.empty || m.head !== v.pos) return null;
    }
    return v;
  },
});

/** `togglePendingEmphasis`: turns italic (`*`) or bold (`**`) on or off for what's typed next at `pos`. */
export function toggledPending(current: PendingEmphasis | null, marker: string, pos: number): PendingEmphasis | null {
  let italic = false, bold = false;
  if (current && current.pos === pos) { italic = current.marker.length % 2 === 1; bold = current.marker.length >= 2; }
  if (marker === '*') italic = !italic; else bold = !bold;
  const n = (italic ? 1 : 0) + (bold ? 2 : 0);
  return n === 0 ? null : { pos, marker: '*'.repeat(n) };
}

// MARK: pending screenplay element (`ScriveningsSession.pending`)

/** A screenplay element the writer chose for a line (Tab, ⌘1–⌘6) before its text shows it. */
export interface PendingElement { lineStart: number; element: ClassifiedElement }
export const setPendingElement = StateEffect.define<PendingElement | null>();
export const pendingElementField = StateField.define<PendingElement | null>({
  create: () => null,
  update(v, tr) {
    for (const e of tr.effects) if (e.is(setPendingElement)) return e.value;
    if (!v || !tr.docChanged) return v;
    // The pending element's line moved or split: forget it (`ScriveningsSession.route`).
    let cleared = false;
    tr.changes.iterChanges((fromA, toA, _fb, _tb, inserted) => {
      if (fromA < v.lineStart || inserted.toString().includes('\n') || tr.startState.doc.sliceString(fromA, toA).includes('\n')) cleared = true;
    });
    return cleared ? null : v;
  },
});

/** Whether a transaction is undo or redo (replaying edits already accepted: never balanced). */
export const isHistory = (tr: Transaction) => tr.isUserEvent('undo') || tr.isUserEvent('redo');

export const stateFields = [layoutField, piecesField, settingsField, pendingEmphasisField, pendingElementField];
